// src/components/profile/BlockButton.tsx

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { blockUser, unblockUser } from "../../services/blockService";
import "./BlockButton.css";

interface BlockButtonProps {
  targetUserId: number;
  initialIsBlocked: boolean;
  // Called after a successful block/unblock so the parent can update its state
  onToggle?: (nowBlocked: boolean) => void;
}

/**
 * Block / Unblock toggle button rendered on profile pages.
 * Styled as a pill chip to match the Follow button and stat chips.
 * Shows a confirmation prompt before blocking to prevent accidental blocks.
 */
const BlockButton: React.FC<BlockButtonProps> = ({
  targetUserId,
  initialIsBlocked,
  onToggle,
}) => {
  const { t } = useTranslation();
  const [isBlocked, setIsBlocked] = useState(initialIsBlocked);
  const [loading, setLoading] = useState(false);
  // Confirmation step: show "Emin misin?" before actually blocking
  const [confirming, setConfirming] = useState(false);

  const handleBlockClick = () => {
    if (loading) return;
    if (isBlocked) {
      // Unblock immediately — no confirmation needed
      handleToggle(false);
    } else {
      // Show confirmation before blocking
      setConfirming(true);
    }
  };

  const handleConfirm = () => {
    setConfirming(false);
    handleToggle(true);
  };

  const handleCancelConfirm = () => {
    setConfirming(false);
  };

  const handleToggle = async (shouldBlock: boolean) => {
    if (loading) return;
    setLoading(true);

    // Optimistic update
    setIsBlocked(shouldBlock);
    onToggle?.(shouldBlock);

    try {
      if (shouldBlock) {
        await blockUser(targetUserId);
      } else {
        await unblockUser(targetUserId);
      }
    } catch {
      // Roll back on failure
      setIsBlocked(!shouldBlock);
      onToggle?.(!shouldBlock);
    } finally {
      setLoading(false);
    }
  };

  // Confirmation state: show compact confirm/cancel pair
  if (confirming) {
    return (
      <div className="block-btn-confirm">
        <span className="block-btn-confirm__label">{t("block_confirm_label")}</span>
        <button
          className="block-btn-confirm__yes"
          onClick={handleConfirm}
          type="button"
        >
          {t("block_confirm_yes")}
        </button>
        <button
          className="block-btn-confirm__no"
          onClick={handleCancelConfirm}
          type="button"
        >
          {t("block_confirm_no")}
        </button>
      </div>
    );
  }

  return (
    <button
      className={`block-btn ${isBlocked ? "block-btn--blocked" : "block-btn--not-blocked"}`}
      onClick={handleBlockClick}
      disabled={loading}
      type="button"
    >
      {isBlocked ? t("block_unblock") : t("block_block")}
    </button>
  );
};

export default BlockButton;